import {
  BORROWER_SIGNER_KEY_PREFIX,
  DEFAULT_DELIVERY_METHOD,
  EConsentPartyType,
  EConsentStatus,
  NBE_SIGNER_KEY_PREFIX,
  SecondFactorType,
  SIGNER_TYPE_BY_BORROWER_TYPE,
  SignerType,
  SSN_LAST4_LENGTH,
} from './borrowersDelivery.constants';
import { DeliveryMethod } from './ecdEstimate.constants';

const onlyDigits = value => String(value ?? '').replace(/\D/g, '');

const getSsnLast4 = value => {
  const digits = onlyDigits(value);

  return digits.length >= SSN_LAST4_LENGTH ? digits.slice(-SSN_LAST4_LENGTH) : null;
};

const maskSsnLast4 = last4 => (last4 ? `SSN •••-${last4}` : 'SSN');

export const formatFactorDisplay = ({ factorType, ssnLast4, zipCode }) => {
  if (factorType === SecondFactorType.ZIP) {
    return zipCode ? `ZIP ${zipCode}` : 'ZIP';
  }

  return maskSsnLast4(ssnLast4);
};

const CONSENT_PARTY_BY_SIGNER_TYPE = {
  [SignerType.PRIMARY_BORROWER]: EConsentPartyType.PRIMARY_BORROWER,
  [SignerType.CO_BORROWER]: EConsentPartyType.CO_BORROWER,
  [SignerType.NBE]: EConsentPartyType.NON_BORROWER,
};

const resolveEConsentStatus = ({ signerType, fullName, consentRecords }) => {
  const targetParty = CONSENT_PARTY_BY_SIGNER_TYPE[signerType];
  const normalizedName = String(fullName ?? '')
    .trim()
    .toLowerCase();

  const match = (consentRecords ?? []).find(record => {
    const nameMatch =
      normalizedName &&
      String(record?.fullName ?? '')
        .trim()
        .toLowerCase() === normalizedName;
    const partyMatch = record?.partyType === targetParty;

    return nameMatch || partyMatch;
  });

  if (!match) {
    return EConsentStatus.PENDING;
  }

  return match.eConsentDate ? EConsentStatus.CAPTURED : EConsentStatus.PENDING;
};

const buildBorrowerSigner = ({ borrower, consentRecords, propertyZip }) => {
  const signerType = SIGNER_TYPE_BY_BORROWER_TYPE[borrower.borrowerType] ?? SignerType.CO_BORROWER;
  const ssnLast4 = getSsnLast4(borrower.ssn ?? borrower.socialSecurityNumber);
  const zipCode = borrower.zip ?? borrower.zipCode ?? propertyZip ?? null;
  const email = borrower.email ?? '';

  return {
    signerKey: `${BORROWER_SIGNER_KEY_PREFIX}:${borrower.borrowerId}`,
    sourceId: borrower.borrowerId,
    signerType,
    fullName: borrower.fullName ?? '',
    email,
    factorType: ssnLast4 ? SecondFactorType.SSN : SecondFactorType.ZIP,
    ssnLast4,
    zipCode,
    eConsentStatus: resolveEConsentStatus({
      signerType,
      fullName: borrower.fullName,
      consentRecords,
    }),
    deliveryMethod: DEFAULT_DELIVERY_METHOD,
    isNbe: false,
  };
};

const buildLoanNbeSigner = ({ nonBorrower, consentRecords, propertyZip }) => {
  const email = nonBorrower.email ?? '';

  return {
    signerKey: `${NBE_SIGNER_KEY_PREFIX}:${nonBorrower.id}`,
    sourceId: nonBorrower.id,
    signerType: SignerType.NBE,
    fullName: nonBorrower.fullName ?? '',
    email,
    factorType: SecondFactorType.ZIP,
    ssnLast4: getSsnLast4(nonBorrower.ssn),
    zipCode: nonBorrower.zip ?? propertyZip ?? null,
    eConsentStatus: resolveEConsentStatus({
      signerType: SignerType.NBE,
      fullName: nonBorrower.fullName,
      consentRecords,
    }),
    deliveryMethod: DEFAULT_DELIVERY_METHOD,
    isNbe: true,
  };
};

export const seedBorrowersDelivery = ({
  borrowers = [],
  nonBorrowers = [],
  consentRecords = [],
  propertyZip = null,
} = {}) => {
  const signers = {};
  const signerOrder = [];

  borrowers.forEach(borrower => {
    if (!borrower?.borrowerId) {
      return;
    }

    const signer = buildBorrowerSigner({ borrower, consentRecords, propertyZip });

    signers[signer.signerKey] = signer;
    signerOrder.push(signer.signerKey);
  });

  nonBorrowers.forEach(nonBorrower => {
    if (!nonBorrower?.id) {
      return;
    }

    const signer = buildLoanNbeSigner({ nonBorrower, consentRecords, propertyZip });

    signers[signer.signerKey] = signer;
    signerOrder.push(signer.signerKey);
  });

  return { isSeeded: true, signerOrder, signers };
};

export const selectOrderedSigners = state =>
  (state?.signerOrder ?? []).map(key => state.signers[key]).filter(Boolean);

// Delivery method is a wizard-local WORKFLOW DECLARATION: it drives the shown
// earliest-closing estimate only and is NOT sent to the backend. The package is
// always delivered electronically; choosing Mail is a reminder to mail printed
// copies and record it via "Mark as mailed" on the Disclosures tracker.
export const setSignerDelivery = (state, signerKey, deliveryMethod) => {
  const signer = state?.signers?.[signerKey];

  if (!signer) {
    return state;
  }

  return {
    ...state,
    signers: {
      ...state.signers,
      [signerKey]: { ...signer, deliveryMethod },
    },
  };
};

export const summarizeDelivery = state => {
  const signers = selectOrderedSigners(state);
  const mailCount = signers.filter(
    ({ deliveryMethod }) => deliveryMethod === DeliveryMethod.MAIL,
  ).length;

  return {
    signerCount: signers.length,
    mailCount,
    esignCount: signers.length - mailCount,
    anyMail: mailCount > 0,
  };
};

// Email and second factor come from the PERSISTED borrower record and are shown
// read-only here (corrections happen on the borrower record before sending, or
// via the post-send remediation actions on the Disclosures tracker). The only
// pre-send gate is that there is at least one signer to send to; the Readiness
// step already hard-blocks on a missing borrower email.
export const isBorrowersDeliveryValid = state => selectOrderedSigners(state).length > 0;
